import React, { Activity, useRef, useState } from 'react';
import { FilterDescriptor } from '@progress/kendo-data-query';
import {
    ChipData,
    FilterCategory,
    FilterHandle,
    IFilterInfoProps,
    IEventBusMessage,
    FilterUIStateUpdate,
    IFilterEventBusMessage,
    InitialExactMatch,
    FILTER_DATA,
    FILTERS,
} from '../shared/interfaces';
import sharedStyles from '../shared/styles.module.scss';
import Button from '@components-clean/core/button';
import { FilterChips } from '@components-clean/core/filter-ui/action-bar/filter/filter-chips';
import { FilterUI } from '@components-clean/core/filter-ui/action-bar/shared/filter-ui';
import {
    buildExactMatchTrackedChange,
    calculateFiltersFromTrackedChanges,
    MapCalculateFiltersToChips,
} from '@components-clean/core/filter-ui/action-bar/shared/filter.service';
import { Popup } from '@components-clean/core/popup/popup';
import Tooltip from '@components-clean/core/tooltip';
import { ComponentNames, useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { busDispatch, useEventBus } from '@utils-clean/eventBus';
import { useTranslation } from '@hooks/index';

type IFilterActionsProps = {
    anchorClass?: string;
    headerExtra?: React.ReactNode;
    initialTrackedChanges?: FilterUIStateUpdate[];
    initialExactMatch?: InitialExactMatch;
    onTrackedChangesChange?: (changes: FilterUIStateUpdate[]) => void;
};

export const FilterActions = ({
    anchorClass,
    headerExtra,
    initialTrackedChanges,
    initialExactMatch,
    onTrackedChangesChange,
}: IFilterActionsProps) => {
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const [show, setShow] = useState(false);
    const [keepUIFilterOpen, setKeepUIFilterOpen] = useState(false);
    const { t } = useTranslation();
    const [categories, setCategories] = useState<Array<FilterCategory>>([]);
    const [data, setData] = useState<
        {
            [p: string]: unknown;
        }[]
    >([]);
    const [selectedChip, setSelectedChip] = useState<ChipData | null>(null);
    const filterUIRef = useRef<FilterHandle | null>(null);
    const filterChipElementsRef = useRef<Map<number, HTMLElement>>(new Map());
    const currentTrackedChangesListRef = useRef<FilterUIStateUpdate[]>(initialTrackedChanges ?? []);
    const gridInfo: IFilterInfoProps = { categories: [], data: [], clearFilter: false };
    const dataLoadingRef = useRef<boolean>(true);
    const hasAppliedInitialTrackedChangesRef = useRef<boolean>(false);

    const { confirmDiscardChanges, componentHasChanges } = useUnsavedChangesContext();

    const applyDefaultExclusions = (categoryList: FilterCategory[]) => {
        categoryList.forEach((category) => {
            const categoriesWithDefaults = category.items.filter(
                (c) =>
                    c.defaultUncheckedIds &&
                    c.defaultUncheckedIds?.length > 0 &&
                    !currentTrackedChangesListRef.current.some(
                        (change) => change.fieldKey === c.fieldKey && change.valueKey === c.valueKey,
                    ),
            );
            categoriesWithDefaults.forEach((cat) => {
                currentTrackedChangesListRef.current.push({
                    fieldKey: cat.fieldKey,
                    valueKey: cat.valueKey,
                    uncheckedValues: [...cat.defaultUncheckedIds!],
                    dataType: cat.dataType,
                    parentId: category.id,
                });
            });
        });
    };

    const applyInitialExactMatch = (categoryList: FilterCategory[], gridData: { [key: string]: unknown }[]) => {
        if (!initialExactMatch) return;

        const parent = categoryList.find((category) =>
            category.items.some(
                (item) => item.fieldKey === initialExactMatch.fieldKey && item.valueKey === initialExactMatch.valueKey,
            ),
        );
        const matchItem = parent?.items.find(
            (item) => item.fieldKey === initialExactMatch.fieldKey && item.valueKey === initialExactMatch.valueKey,
        );
        if (!parent || !matchItem) return;

        // The source screen only knows the value to keep, not every value to uncheck, so compute the
        // unchecked values here against the full dataset the destination grid has just provided.
        const exactMatchChange = buildExactMatchTrackedChange(gridData, matchItem, initialExactMatch.value, parent.id);
        currentTrackedChangesListRef.current = [
            ...currentTrackedChangesListRef.current.filter(
                (change) => change.fieldKey !== matchItem.fieldKey || change.valueKey !== matchItem.valueKey,
            ),
            exactMatchChange,
        ];
    };

    useEventBus<IEventBusMessage>(FILTER_DATA, (gridData) => {
        if (gridData.clearFilter) currentTrackedChangesListRef.current = [];

        setCategories(gridData.categories);
        setData(gridData.data);
        applyDefaultExclusions(gridData.categories);

        if (!hasAppliedInitialTrackedChangesRef.current && initialExactMatch && gridData.data.length) {
            applyInitialExactMatch(gridData.categories, gridData.data);
        }

        onTrackedChangesChange?.(currentTrackedChangesListRef.current);
        dataLoadingRef.current = false;

        if (!hasAppliedInitialTrackedChangesRef.current && currentTrackedChangesListRef.current.length) {
            hasAppliedInitialTrackedChangesRef.current = true;
            busDispatch<IFilterEventBusMessage>(
                FILTERS,
                calculateFiltersFromTrackedChanges(
                    currentTrackedChangesListRef.current,
                    undefined,
                    gridData.categories,
                ),
            );
        }
    });

    const onFilterClick = () => {
        if (!show) {
            setShow(true);
        }
    };

    const discardCommentSideBarChanges = async () => {
        if (!componentHasChanges(ComponentNames.commentsSideBar)) {
            return true;
        }
        setKeepUIFilterOpen(true);
        const discard = await confirmDiscardChanges();
        setKeepUIFilterOpen(false);
        return discard;
    };

    const onApply = async (filters: FilterDescriptor[]) => {
        if (!(await discardCommentSideBarChanges())) {
            return;
        }

        setShow(false);
        busDispatch<IFilterEventBusMessage>(FILTERS, filters);
        onTrackedChangesChange?.(currentTrackedChangesListRef.current);
    };

    const onResetFiltersClick = async () => {
        if (!(await discardCommentSideBarChanges())) {
            return;
        }

        currentTrackedChangesListRef.current = [];
        busDispatch<IFilterEventBusMessage>(FILTERS, []);
        onTrackedChangesChange?.([]);
    };

    const onFilterChipClick = (chip: ChipData) => {
        setShow(true);
        if (filterUIRef.current) filterUIRef.current?.setSelectedCategory(chip.parentCategory, chip.categoryId);
        else setSelectedChip(chip);
    };

    return (
        <div className="k-d-flex k-gap-1 k-flex-auto" style={{ minWidth: '0' }}>
            <div ref={anchorRef} className={anchorClass}>
                <Button
                    data-testid="feGrid-filter-btn"
                    themeColor="secondary"
                    faIcon={['far', 'filter']}
                    faIconStyle={{ width: 16, height: 16 }}
                    disabled={dataLoadingRef.current}
                    onClick={onFilterClick}
                    className={sharedStyles.actionBarButtons}
                    size="small"
                />
            </div>
            <Tooltip parentTitle position="bottom">
                <Button
                    data-testid="feGrid-clear-filter-btn"
                    faIcon={['far', 'filter-slash']}
                    className={sharedStyles.actionBarButtons}
                    faIconStyle={{ width: 20, height: 16 }}
                    size="small"
                    disabled={currentTrackedChangesListRef.current.length === 0}
                    onClick={onResetFiltersClick}
                    themeColor="secondary"
                    fillMode="outline"
                    title={t('clearFilters')}
                />
            </Tooltip>
            <FilterChips
                onClick={onFilterChipClick}
                filterChipElements={filterChipElementsRef}
                chipData={MapCalculateFiltersToChips(currentTrackedChangesListRef.current, categories)}
            />
            <Activity mode={gridInfo.data ? 'visible' : 'hidden'}>
                <Popup
                    data-testid="feGrid-filter-popup"
                    anchor={anchorRef?.current}
                    show={show}
                    border="topBorder"
                    alternativeStyle
                    closeOnBlur={false}
                    style={{
                        marginTop: '6px',
                    }}
                    popupCard
                >
                    <FilterUI
                        key={data.length}
                        data-testid="feGrid-filter-id"
                        categories={categories}
                        data={data}
                        onApply={onApply}
                        onClose={() => {
                            if (!keepUIFilterOpen) {
                                setSelectedChip(null);
                                setShow(false);
                            }
                        }}
                        defaultCategory={selectedChip}
                        ref={filterUIRef}
                        filterChipElements={filterChipElementsRef}
                        currentTrackedChangesList={currentTrackedChangesListRef}
                        headerExtra={headerExtra}
                    />
                </Popup>
            </Activity>
        </div>
    );
};
